<template>
  <div>
    <v-dialog
      v-model="modalStatus"
      :max-width="$vuetify.breakpoint.mobile ? undefined : '780'"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
    >
      <v-card class="modal-card" elevation="0">
        <div class="modal-header">
          <div class="modal-header-left">
            <div class="modal-icon">
              <v-icon color="white" size="16">mdi-book-open-page-variant-outline</v-icon>
            </div>
            <div>
              <p class="modal-title">Crear Folio</p>
              <p class="modal-subtitle">
                <span>{{ $store.userStore.state.userInfo.name }}</span>
                <span class="separator">·</span>
                <span>{{ book ? book.name : 'Selecciona un Libro' }}</span>
              </p>
            </div>
          </div>
          <div class="modal-header-actions">
            <button class="header-btn close" type="button" @click="close" title="Cerrar">
              <v-icon size="14" color="white">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <v-card-text class="modal-body pa-0">
          <v-form ref="form" v-model="valid">
            <div class="form-section">
              <v-select
                v-model="selectedBookId"
                :items="books"
                item-text="name"
                item-value="id"
                label="Libro"
                outlined
                dense
                hide-details="auto"
                :loading="loadingBooks"
                :rules="[value => !!value || 'Selecciona un Libro']"
              />
            </div>

            <div class="form-section">
              <v-text-field
                v-model="form.title"
                placeholder="Asunto de la comunicación"
                outlined
                dense
                hide-details="auto"
                :rules="requiredRule"
                class="title-input"
              />
            </div>

            <div class="form-section">
              <label class="field-label">Descripción</label>
              <InteractionEditor
                v-model="form.description"
                item_type="communication_folio"
                :allow-inline-images="false"
              />
              <p v-if="descriptionError" class="caption error--text mb-0 mt-1">La descripción es obligatoria.</p>
            </div>

            <div class="form-section form-grid">
              <v-autocomplete
                v-model="form.responsable"
                :items="responsibleUsers"
                item-text="name"
                item-value="id"
                label="Responsable"
                placeholder="Seleccionar responsable"
                outlined
                dense
                clearable
                hide-details="auto"
                :loading="$store.usersStore.state.loadingUserList"
              >
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ data.item.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-select
                v-model="form.priority"
                :items="priorityOptions"
                item-text="text"
                item-value="value"
                label="Urgencia"
                outlined
                dense
                hide-details="auto"
              />
            </div>

            <div class="form-section">
              <label class="field-label">Asociaciones</label>
              <ItemsAssociationInputSelect :change-items="setAssociations" />
              <div v-if="form.associations.length" class="caption text--secondary mt-2">
                {{ form.associations.length }} asociación(es) seleccionada(s)
              </div>
            </div>

            <div class="form-section">
              <label class="field-label">Archivos</label>
              <div class="attachments-row">
                <button class="attach-btn" type="button" @click="$refs.fileInput.click()">
                  <v-icon small color="#2D4B95">mdi-paperclip</v-icon>
                  Adjuntar archivos
                </button>
                <input ref="fileInput" class="d-none" type="file" multiple @change="setFiles">
                <div v-for="file in form.files" :key="file.name + file.size" class="file-chip">
                  <span>{{ file.name }}</span>
                  <button class="chip-remove" type="button" @click="removeFile(file)">
                    <v-icon x-small>mdi-close</v-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <div class="footer-actions">
                <button class="btn-cancel" type="button" :disabled="saving" @click="close">Cancelar</button>
                <button class="btn-confirm" type="button" :disabled="saving" @click="preSubmit">
                  <v-progress-circular v-if="saving" :size="14" :width="2" color="white" indeterminate class="mr-1" />
                  <v-icon v-else small color="white" class="mr-1">mdi-publish</v-icon>
                  Publicar Folio
                </button>
              </div>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import ItemsAssociationInputSelect from '@/components/Globals/Helpers/ItemsAssociationInputSelect'
import { create as createDraftAPI, update as updateDraftAPI } from '@/helpers/api/drafts'

const emptyForm = () => ({
  title: '',
  description: '',
  responsable: null,
  priority: 2,
  associations: [],
  files: [],
})

export default {
  name: 'CreateCommunicationFolioModal',
  components: { InteractionEditor, ItemsAssociationInputSelect },
  props: {
    modalStatus: { type: Boolean, default: false },
    closeModal: { type: Function, required: true },
    draftToLoad: { type: Object, default: null },
  },
  data: () => ({
    valid: false,
    descriptionError: false,
    form: emptyForm(),
    draftId: null,
    draftCreated: false,
    draftSaveTimer: null,
    handleFormChangeDebounced: null,
    isResetting: false,
  }),
  computed: {
    book() {
      const { books, selectedBookId } = this.$store.communicationBooksStore.state
      return books.find(book => book.id === selectedBookId) || null
    },
    books() {
      return this.$store.communicationBooksStore.state.books
    },
    selectedBookId: {
      get() {
        return this.$store.communicationBooksStore.state.selectedBookId
      },
      set(bookId) {
        this.$store.communicationBooksStore.commit('setSelectedBookId', bookId)
      },
    },
    loadingBooks() {
      return this.$store.communicationBooksStore.state.loadingBooks
    },
    saving() {
      return this.$store.communicationBooksStore.state.saving
    },
    requiredRule() {
      return [value => !!String(value || '').trim() || 'Campo obligatorio']
    },
    responsibleUsers() {
      return this.$store.usersStore.state.usersList || []
    },
    priorityOptions() {
      return [{ text: 'Baja', value: 1 }, { text: 'Normal', value: 2 }, { text: 'Alta', value: 3 }]
    },
  },
  watch: {
    modalStatus(open) {
      if (open) {
        this.form = emptyForm()
        this.descriptionError = false
        this.draftId = null
        this.draftCreated = false
        this.isResetting = false
        this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
        this.ensureBooksLoaded()
        this.ensureUsersLoaded()
        if (this.draftToLoad) this.loadDraft(this.draftToLoad)
      }
    },
    draftToLoad(draft) {
      if (draft && this.modalStatus) this.loadDraft(draft)
    },
    form: {
      handler() {
        if (!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced()
      },
      deep: true,
    },
  },
  mounted() {
    this.handleFormChangeDebounced = this.debounce(() => this.createDraft(), 1000)
    if (this.modalStatus) this.ensureBooksLoaded()
    if (this.draftToLoad) this.loadDraft(this.draftToLoad)
  },
  beforeDestroy() {
    this.clearDraftTimer()
  },
  methods: {
    async ensureBooksLoaded() {
      const communicationStore = this.$store.communicationBooksStore
      const project = this.$store.projectsStore.state.selectedProject
      if (!project || communicationStore.state.books.length) return
      await communicationStore.dispatch('loadBooks', project.id)
    },
    async ensureUsersLoaded() {
      const project = this.$store.projectsStore.state.selectedProject
      if (!project || this.responsibleUsers.length) return
      await this.$store.usersStore.dispatch('getUsersList', { project_id: project.id })
    },
    debounce(fn, wait) {
      let timeout = null
      const debounced = (...args) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => fn(...args), wait)
      }
      debounced.cancel = () => clearTimeout(timeout)
      return debounced
    },
    clearDraftTimer() {
      if (this.draftSaveTimer) clearInterval(this.draftSaveTimer)
      this.draftSaveTimer = null
      if (this.handleFormChangeDebounced) this.handleFormChangeDebounced.cancel()
    },
    close() {
      if (!this.saving) {
        this.clearDraftTimer()
        this.$store.communicationBooksStore.commit('setDraftToLoad', null)
        this.closeModal()
      }
    },
    setAssociations(associations) {
      this.form.associations = Array.isArray(associations) ? associations : []
    },
    setFiles(event) {
      this.form.files = this.form.files.concat(Array.from(event.target.files || []))
      event.target.value = ''
    },
    removeFile(file) {
      this.form.files = this.form.files.filter(item => item !== file)
    },
    hasDescription() {
      const plainText = String(this.form.description || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      return plainText.length > 0
    },
    validateForm() {
      const validForm = this.$refs.form ? this.$refs.form.validate() : false
      this.descriptionError = !this.hasDescription()
      return validForm && !this.descriptionError && !!this.book
    },
    buildDraftData() {
      return {
        book_id: this.book ? this.book.id : null,
        form: {
          title: this.form.title,
          description: this.form.description,
          responsable: this.form.responsable,
          priority: this.form.priority,
          associations: this.form.associations.map(item => ({ type: item.type, id: item.id })),
        },
      }
    },
    async createDraft() {
      if (this.draftCreated || !this.book || !this.form.title) return this.draftId
      this.draftCreated = true
      try {
        const response = await createDraftAPI({
          type: 15,
          project_id: this.$store.projectsStore.state.selectedProject.id,
          data: JSON.stringify(this.buildDraftData()),
        })
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
        } else if (response.offline) {
          this.draftId = response.draft?.id || null
        } else if (response.code === 403) {
          // Se mantiene draftCreated para no reintentar ni avisar en cada cambio del formulario.
          this.$store.communicationBooksStore.commit('setActionDenied', {
            message: response.message || 'No tienes permisos para crear borradores.',
            key: Date.now(),
          })
        } else {
          this.draftCreated = false
        }
      } catch (error) {
        this.draftCreated = false
        console.error('Error al crear borrador de Folio:', error)
      }
      return this.draftId
    },
    startDraftUpdateTimer() {
      this.clearDraftTimer()
      this.draftSaveTimer = setInterval(() => this.updateDraft(), 20000)
    },
    async updateDraft() {
      if (!this.draftId || String(this.draftId).startsWith('local_')) return
      const response = await updateDraftAPI({
        id: this.draftId,
        type: 15,
        data: JSON.stringify(this.buildDraftData()),
      })
      if (response && response.code === 403) {
        this.clearDraftTimer()
        this.$store.communicationBooksStore.commit('setActionDenied', {
          message: response.message || 'No tienes permisos para editar este borrador.',
          key: Date.now(),
        })
      }
    },
    loadDraft(draft) {
      if (!draft) return
      let data = draft.data
      if (typeof data === 'string') {
        try { data = JSON.parse(data) } catch (error) { data = {} }
      }
      const bookId = data.book_id || data.form?.book_id
      if (bookId) this.$store.communicationBooksStore.commit('setSelectedBookId', bookId)
      const form = data.form || data
      this.isResetting = true
      this.form = {
        ...emptyForm(),
        title: form.title || '',
        description: form.description || '',
        responsable: form.responsable || null,
        priority: Number(form.priority || 2),
        associations: Array.isArray(form.associations) ? form.associations : [],
      }
      this.draftId = draft.id
      this.draftCreated = true
      this.startDraftUpdateTimer()
      this.$nextTick(() => { this.isResetting = false })
    },
    preSubmit() {
      if (!this.validateForm()) return
      return this.submit()
    },
    async submit() {
      try {
        await this.createDraft()
        let folio
        const hasRemoteDraft = this.draftId && !String(this.draftId).startsWith('local_')
        if (hasRemoteDraft) {
          await this.updateDraft()
          const data = new FormData()
          data.append('draft_id', this.draftId)
          this.form.files.forEach(file => data.append('files[]', file))
          folio = await this.$store.communicationBooksStore.dispatch('publishDraft', {
            bookId: this.book.id,
            data,
          })
        } else {
          const data = new FormData()
          data.append('title', this.form.title)
          data.append('description', this.form.description)
          if (this.form.responsable) data.append('responsable', this.form.responsable)
          data.append('priority', this.form.priority)
          data.append('associations', JSON.stringify(this.form.associations.map(item => ({ type: item.type, id: item.id }))))
          this.form.files.forEach(file => data.append('files[]', file))
          folio = await this.$store.communicationBooksStore.dispatch('publishFolio', { bookId: this.book.id, data })
        }
        if (!folio) return
        this.close()
      } catch (error) {
        this.descriptionError = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Card / Header (shared with CreateTareaModal) ── */
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #2D4B95;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}
.modal-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.modal-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.modal-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: white;
  line-height: 1.2;
}
.modal-subtitle {
  margin: 0;
  font-size: 11px;
  color: rgba(255,255,255,0.65);
  line-height: 1.2;
}
.modal-subtitle .separator {
  margin: 0 5px;
  opacity: 0.5;
}
.modal-header-actions { display: flex; gap: 6px; }
.header-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.header-btn.close { background: rgba(244,81,108,0.9); color: white; }
.header-btn.close:hover { background: rgba(244,81,108,1); }

/* ── Body ── */
.modal-body {
  overflow-y: auto;
  max-height: calc(90vh - 80px);
}
.form-section {
  padding: 14px 20px;
  border-bottom: 1px solid #F3F4F6;
}
.form-section:last-child { border-bottom: none; }
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #2D4B95;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

/* ── Attachments ── */
.attachments-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.attach-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 7px;
  border: 1.5px solid #D7DEEC;
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #2D4B95;
  transition: all 0.15s;
}
.attach-btn:hover { border-color: #2D4B95; background: #F5F7FF; }
.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  background: #EEF2FC;
  font-size: 12px;
  color: #2D4B95;
}
.chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

/* ── Footer ── */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #F9FAFB;
  border-top: 1px solid #E5E7EB;
}
.footer-actions { display: flex; gap: 8px; align-items: center; }
.btn-cancel {
  padding: 7px 16px;
  border-radius: 7px;
  border: 1.5px solid #E5E7EB;
  background: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  transition: all 0.15s;
}
.btn-cancel:hover { border-color: #D1D5DB; background: #F3F4F6; }
.btn-confirm {
  padding: 7px 20px;
  border-radius: 7px;
  border: none;
  background: #2D4B95;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  transition: all 0.15s;
}
.btn-confirm:hover { background: #253d7a; }
.btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
